/*
  Smooche cart gift guard.

  Kaching Bundles adds free-gift lines next to a bundle parent and marks both with the
  hidden line property __kaching_bundles: {id, deal, v, main: true} on the parent and
  {id, deal, v, gift} on each gift. Shopify's Kaching discount function prices the gift
  lines at $0 while their parent is in the cart. Kaching does not remove gift lines when
  the parent leaves the cart through the theme, and a gift without its parent becomes a
  paid line. This script keeps gifts consistent using only that metadata:

  1. Removing a bundle parent removes every line of that bundle in the same request: a
     cart/change that zeroes a parent line is rewritten to cart/update covering the
     parent, its gifts and any add-on sharing the bundle id.
  2. A gift line whose parent is missing is removed: at once when that parent was seen in
     this page's cart before, otherwise after a grace period, because Kaching adds gift
     lines about a second before the parent arrives and can hold both behind its popup.
     The grace is short on purpose: an orphan gift is usually a paid line, and it must
     not reach checkout.
  3. A gift line that would be charged while its parent is present is reduced to one unit
     and, if still charged, removed.

  Nothing is keyed on product or variant ids. Lines without Kaching gift metadata are
  never touched. Corrections refresh the drawer without opening it and never hold a
  checkout click for longer than a bounded wait.
*/
(() => {
  'use strict';
  if (window.smoocheCartGiftGuardReady) return;
  window.smoocheCartGiftGuardReady = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartUrl = `${root}cart.js`;
  const changeUrl = `${root}cart/change.js`;
  const updateUrl = `${root}cart/update.js`;
  const originalFetch = window.fetch.bind(window);
  const timeouts = Object.assign(
    {request: 6000, checkout: 5000, debounce: 150, orphanGrace: 5000},
    window.smoocheGiftGuardTimeouts
  );
  const Deadline = typeof AbortController === 'function' ? AbortController : window.AbortController;
  const isCartMutation = (url) => /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url || '');
  const isCartChange = (url) => /\/cart\/change(?:\.js)?(?:\?|$)/.test(url || '');

  const bundleOf = (item) => {
    try {
      return JSON.parse(item && item.properties && item.properties.__kaching_bundles) || null;
    } catch (_) {
      return null;
    }
  };
  const giftBundle = (item) => {
    const bundle = bundleOf(item);
    return bundle && bundle.id && bundle.gift ? bundle : null;
  };
  const parentBundle = (item) => {
    const bundle = bundleOf(item);
    return bundle && bundle.id && bundle.main === true ? bundle : null;
  };

  function fetchWithDeadline(url, options = {}) {
    if (typeof Deadline !== 'function') return originalFetch(url, options);
    const controller = new Deadline();
    const timer = window.setTimeout(() => controller.abort(), timeouts.request);
    return originalFetch(url, {...options, signal: controller.signal})
      .finally(() => window.clearTimeout(timer));
  }

  async function readCart() {
    const response = await fetchWithDeadline(cartUrl, {credentials: 'same-origin', cache: 'no-store'});
    if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
    return response.json();
  }

  async function postJson(url, body) {
    const response = await fetchWithDeadline(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Cart correction failed: ${response.status}`);
    const result = await response.json();
    if (result && result.errors) throw new Error('Cart correction was rejected');
    return result;
  }

  // ---- Rules -------------------------------------------------------------------------

  const seenParents = new Set(); // bundle ids that had a parent line in this page's cart
  const orphanSince = new Map(); // gift line key -> when it was first seen without a parent

  // Returns {change} for the next cart/change to apply, or {waitFor} in ms when an orphan
  // is still inside its grace period, or {} when the cart is consistent.
  function nextCorrection(cart, now) {
    const items = cart.items || [];
    const parents = new Set();
    for (const item of items) {
      const bundle = parentBundle(item);
      if (bundle) {
        parents.add(bundle.id);
        seenParents.add(bundle.id);
      }
    }
    for (const key of Array.from(orphanSince.keys())) {
      if (!items.some((item) => item.key === key)) orphanSince.delete(key);
    }
    let waitFor = 0;
    for (const item of items) {
      const bundle = giftBundle(item);
      if (!bundle) continue;
      if (!parents.has(bundle.id)) {
        if (seenParents.has(bundle.id)) return {change: {id: item.key, quantity: 0}};
        const since = orphanSince.get(item.key) || now;
        orphanSince.set(item.key, since);
        const remaining = timeouts.orphanGrace - (now - since);
        if (remaining <= 0) return {change: {id: item.key, quantity: 0}};
        waitFor = waitFor ? Math.min(waitFor, remaining) : remaining;
        continue;
      }
      orphanSince.delete(item.key);
      if (Number(item.final_line_price) > 0) {
        if (Number(item.quantity) > 1) return {change: {id: item.key, quantity: 1}};
        return {change: {id: item.key, quantity: 0}};
      }
    }
    return {waitFor};
  }

  // ---- Correction loop ---------------------------------------------------------------

  let timer = null;
  let active = null;
  let dirty = false;

  async function correct() {
    let cart = await readCart();
    let changed = false;
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const next = nextCorrection(cart, Date.now());
      if (!next.change) {
        if (next.waitFor) schedule(next.waitFor + 50);
        return changed;
      }
      cart = await postJson(changeUrl, next.change);
      changed = true;
    }
    throw new Error('Gift correction did not settle');
  }

  function refreshDrawer() {
    if (window.cart && typeof window.cart.getCart === 'function') {
      window.cart.getCart(false);
    } else if (window.location.pathname.replace(/\/$/, '') === `${root}cart`.replace(/\/$/, '')) {
      window.location.reload();
    }
  }

  function flush() {
    if (active) return active;
    active = (async () => {
      while (dirty) {
        dirty = false;
        if (await correct()) refreshDrawer();
      }
    })().finally(() => {
      active = null;
    });
    return active;
  }

  function schedule(delay = timeouts.debounce) {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = null;
      dirty = true;
      flush().catch((error) => console.error('Smooche gift guard:', error));
    }, delay);
  }

  // ---- Atomic bundle removal ---------------------------------------------------------

  function parseChangeBody(body) {
    try {
      if (!body) return null;
      if (typeof body === 'string') {
        const trimmed = body.trim();
        if (trimmed.startsWith('{')) return JSON.parse(trimmed);
        return Object.fromEntries(new URLSearchParams(trimmed));
      }
      if (typeof URLSearchParams === 'function' && body instanceof URLSearchParams) {
        return Object.fromEntries(body);
      }
      if (typeof FormData === 'function' && body instanceof FormData) {
        return Object.fromEntries(body.entries());
      }
    } catch (_) {}
    return null;
  }

  // The theme addresses cart/change by line position, which goes stale whenever something
  // else (an app, this guard) changed the cart since the drawer last rendered. Remember what
  // the shopper actually clicked so the request can be re-targeted by line key.
  let lastIntent = null;
  document.addEventListener('click', (event) => {
    const remove = event.target && event.target.closest && event.target.closest('[data-item-remove][data-id]');
    if (remove) lastIntent = {key: remove.getAttribute('data-id'), quantity: 0, at: Date.now()};
  }, true);
  document.addEventListener('theme:cart:update', (event) => {
    const detail = event.detail || {};
    if (detail.id) lastIntent = {key: String(detail.id), quantity: Number(detail.quantity), at: Date.now()};
  }, true);

  function targetOfChange(items, data) {
    if (data.id !== undefined && data.id !== null && data.id !== '') {
      const id = String(data.id);
      return items.find((item) => item.key === id) ||
        items.find((item) => String(item.variant_id) === id) || null;
    }
    const line = Number(data.line);
    const byIndex = line > 0 ? items[line - 1] || null : null;
    if (lastIntent && Date.now() - lastIntent.at < 5000 && Number(data.quantity) === lastIntent.quantity) {
      const byKey = items.find((item) => item.key === lastIntent.key);
      if (byKey) return byKey;
    }
    return byIndex;
  }

  function postLike(options, url, body) {
    return originalFetch(url, {
      method: 'POST',
      credentials: options.credentials || 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body),
    });
  }

  // A zero-quantity change on a bundle parent becomes one cart/update that removes the
  // parent and every line sharing its bundle id. A change whose line position no longer
  // matches the clicked line is re-addressed by key. Anything else passes through.
  async function rewriteCartChange(args) {
    const options = args[1] || {};
    const data = parseChangeBody(options.body);
    if (!data || data.quantity === undefined) return null;
    const cart = await readCart();
    const items = cart.items || [];
    const target = targetOfChange(items, data);
    if (!target) return null;
    const quantity = Number(data.quantity);
    const bundle = quantity === 0 ? parentBundle(target) : null;
    if (bundle) {
      const updates = {};
      for (const item of items) {
        const other = bundleOf(item);
        if (other && other.id === bundle.id) updates[item.key] = 0;
      }
      if (Object.keys(updates).length > 1) return postLike(options, updateUrl, {updates});
    }
    const line = Number(data.line);
    const byIndex = line > 0 ? items[line - 1] : null;
    if (data.id === undefined && byIndex && byIndex.key !== target.key) {
      return postLike(options, changeUrl, {id: target.key, quantity});
    }
    return null;
  }

  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
    const method = String((args[1] && args[1].method) || (args[0] && args[0].method) || 'GET').toUpperCase();
    let result;
    if (isCartChange(url) && method === 'POST') {
      result = rewriteCartChange(args)
        .catch(() => null)
        .then((rewritten) => rewritten || originalFetch(...args));
    } else {
      result = originalFetch(...args);
    }
    if (isCartMutation(url)) {
      result.then((response) => { if (response.ok) schedule(); }).catch(() => {});
    }
    return result;
  };

  ['theme:cart:loaded', 'theme:cart:added'].forEach((name) => {
    document.addEventListener(name, () => schedule());
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule(), {once: true});
  } else {
    schedule();
  }

  // ---- Checkout ----------------------------------------------------------------------

  // Only when a correction is pending: give it a bounded head start, then let checkout go.
  function settleBeforeCheckout() {
    if (!active && !timer) return null;
    if (timer) {
      window.clearTimeout(timer);
      timer = null;
      dirty = true;
    }
    const pending = flush().catch((error) => console.error('Smooche gift guard:', error));
    const deadline = new Promise((resolve) => window.setTimeout(resolve, timeouts.checkout));
    return Promise.race([pending, deadline]);
  }

  let replayingCheckout = false;

  document.addEventListener('click', (event) => {
    if (replayingCheckout) return;
    const button = event.target.closest('[data-cart-form] [name="checkout"]');
    if (!button || button.disabled) return;
    const pending = settleBeforeCheckout();
    if (!pending) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    pending.then(() => {
      replayingCheckout = true;
      try {
        const current = button.isConnected ? button : document.querySelector('[data-cart-form] [name="checkout"]');
        if (current) current.click();
      } finally {
        replayingCheckout = false;
      }
    });
  }, true);

  document.addEventListener('submit', (event) => {
    if (replayingCheckout || !event.target.matches('[data-cart-form]')) return;
    const form = event.target;
    const button = event.submitter || form.querySelector('[name="checkout"]');
    if (!button || button.name !== 'checkout') return;
    const pending = settleBeforeCheckout();
    if (!pending) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    pending.then(() => {
      replayingCheckout = true;
      try {
        form.requestSubmit(button);
      } finally {
        replayingCheckout = false;
      }
    });
  }, true);
})();
